<template>
  <el-dialog
    :visible.sync="visible"
    :title="dialogTitle"
    width="960px"
    @close="handleClose"
  >
    <div class="toolbar">
      <el-select v-model="period" size="mini" style="width: 120px" @change="handlePeriodChange">
        <el-option label="今天" value="TODAY" />
        <el-option label="昨日" value="YESTERDAY" />
        <el-option label="近7天" value="7D" />
        <el-option label="近30天" value="30D" />
      </el-select>
      <el-input
        v-model="counterpartyUserId"
        size="mini"
        clearable
        placeholder="对手ID"
        style="width: 180px"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-button size="mini" type="primary" @click="handleSearch">查询</el-button>
    </div>

    <div class="summary-bar">
      <span>流入：<em class="amount-positive">{{ formatAmount(summary.addAmount) }}</em></span>
      <span>流出：<em class="amount-negative">{{ formatAmount(summary.subAmount) }}</em></span>
      <span>净额：<em :class="amountClass(summary.netAmount)">{{ formatAmount(summary.netAmount) }}</em></span>
    </div>

    <el-table :data="list" style="width: 100%" max-height="420">
      <el-table-column prop="createTime" label="时间" width="170">
        <template slot-scope="scope">
          {{ formatTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="counterpartyUserId" label="对手ID" width="120">
        <template slot-scope="scope">
          {{ scope.row.counterpartyUserId == null ? '-' : scope.row.counterpartyUserId }}
        </template>
      </el-table-column>
      <el-table-column prop="bizId" label="业务ID" min-width="140" show-overflow-tooltip />
      <el-table-column prop="coin" label="币种" width="90" />
      <el-table-column prop="billTypeName" label="类型" min-width="140" show-overflow-tooltip />
      <el-table-column prop="side" label="方向" width="80">
        <template slot-scope="scope">
          <span :class="sideClass(scope.row.side)">{{ formatSide(scope.row.side) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="amount" label="金额" width="140">
        <template slot-scope="scope">
          <span :class="sideClass(scope.row.side)">{{ formatAmount(scope.row.amount) }}</span>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      :key="paginationKey"
      :size="pagination.size"
      :page="pagination.page"
      :total="pagination.total"
      @pageChanged="handlePageChanged"
    />
  </el-dialog>
</template>

<script>
import Pagination from 'components/pagination';
import api from '../api';
import { formatPlainAmount } from '../../common/amount';

export default {
  name: 'AccountBillDetailDialog',
  components: {
    Pagination,
  },
  data() {
    return {
      visible: false,
      period: 'TODAY',
      context: null,
      summary: {
        addAmount: 0,
        subAmount: 0,
        netAmount: 0,
        timeLabel: '今天',
        coin: '',
      },
      list: [],
      pagination: {
        page: 1,
        size: 20,
        total: 0,
      },
      paginationKey: 0,
      counterpartyUserId: '',
    };
  },
  computed: {
    dialogTitle() {
      if (!this.context) return '流水明细';
      const parts = [
        this.context.userTypeName || '公户',
        this.context.userId,
        this.context.coin,
      ];
      return parts.filter(Boolean).join(' · ');
    },
  },
  methods: {
    open(row, coin) {
      this.context = {
        userId: row.userId,
        userTypeName: row.userTypeName,
        coin,
      };
      this.period = 'TODAY';
      this.counterpartyUserId = '';
      this.pagination = { page: 1, size: 20, total: 0 };
      this.paginationKey += 1;
      this.visible = true;
      this.fetchDetail(1, 20);
    },
    handleClose() {
      this.visible = false;
      this.list = [];
      this.context = null;
      this.counterpartyUserId = '';
    },
    handlePeriodChange() {
      this.pagination.page = 1;
      this.paginationKey += 1;
      this.fetchDetail(1, this.pagination.size);
    },
    handleSearch() {
      this.pagination.page = 1;
      this.paginationKey += 1;
      this.fetchDetail(1, this.pagination.size);
    },
    parseCounterpartyUserId() {
      const raw = String(this.counterpartyUserId || '').trim();
      if (!raw) return '';
      if (!/^\d+$/.test(raw)) {
        this.$message.warning('对手ID须为数字');
        return null;
      }
      return raw;
    },
    fetchDetail(page = 1, size = 20) {
      if (!this.context) return;
      const counterpartyUserId = this.parseCounterpartyUserId();
      if (counterpartyUserId === null) return;
      api.billDetail({
        userId: this.context.userId,
        coin: this.context.coin,
        period: this.period,
        counterpartyUserId,
        page,
        size,
      }).then((res) => {
        if (res.code !== 0 || !res.data) return;
        const data = res.data;
        this.summary = {
          addAmount: data.addAmount,
          subAmount: data.subAmount,
          netAmount: data.netAmount,
          timeLabel: data.timeLabel,
          coin: data.coin || this.context.coin,
        };
        this.list = (data.page && data.page.list) || [];
        this.pagination.total = (data.page && data.page.totalCount) || 0;
        this.pagination.page = page;
        this.pagination.size = size;
      });
    },
    handlePageChanged({ page, size }) {
      this.fetchDetail(page, size);
    },
    formatAmount(value) {
      return formatPlainAmount(value);
    },
    formatTime(value) {
      if (!value) return '-';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    formatSide(side) {
      return side === 'ADD' ? '流入' : side === 'SUB' ? '流出' : side || '-';
    },
    amountClass(value) {
      return Number(value) >= 0 ? 'amount-positive' : 'amount-negative';
    },
    sideClass(side) {
      return side === 'ADD' ? 'amount-positive' : 'amount-negative';
    },
  },
};
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.summary-bar {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  font-size: 14px;
}

.summary-bar em {
  font-style: normal;
  font-weight: 600;
}

.amount-positive {
  color: #67c23a;
}

.amount-negative {
  color: #f56c6c;
}
</style>
